import * as S from './styles';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { shallowEqual } from 'react-redux';
import Modal from 'components/Modal';
import VideoPlayer, { VideoPlayerRef } from 'components/Widget/VideoPlayer';
import forge from 'apis/forge';
import { LearningCenterVideo } from 'dragonmouse-shared/interfaces';
import { RootReducer } from 'redux/reducers';
import { setLearningCenterState } from 'redux/mode/actions';
import Icon, { IconType } from 'components/Icon';
import { selectUser } from 'redux/db/reducers';
import Button, { ButtonState, ButtonType } from 'components/Widget/Button/Button';
import { unthemed } from 'tokens';
import { trackLearningAction } from 'analytics';

export interface LearningCenterEntry {
	id: LearningCenterVideo;
	title: string;
	description: string;
	url: string;
	thumbnail: string;
	duration: string;
}

export const ENTRIES: LearningCenterEntry[] = [
	{
		id: 'intro' as LearningCenterVideo,
		title: 'Intro to Moonchild',
		description: 'Get started with your first design',
		url: 'https://cdn.moonchild.ai/onboarding/intro-1.mp4',
		thumbnail: 'https://cdn.moonchild.ai/onboarding/intro-1.jpg',
		duration: '4:43',
	},
	{
		id: 'canvas' as LearningCenterVideo,
		title: 'Chat, Merge, and Iterate',
		description: 'Learn the fundamentals of design creation',
		url: 'https://cdn.moonchild.ai/onboarding/chat-1.mp4',
		thumbnail: 'https://cdn.moonchild.ai/onboarding/chat-1.jpg',
		duration: '3:12',
	},
	{
		id: 'design_system' as LearningCenterVideo,
		title: 'Design Systems',
		description: 'Learn to chat with your designs',
		url: 'https://cdn.moonchild.ai/onboarding/design-system-1.mp4',
		thumbnail: 'https://cdn.moonchild.ai/onboarding/design-system-1.jpg',
		duration: '4:36',
	},
	{
		id: 'export' as LearningCenterVideo,
		title: 'Preview, Edit, and Export',
		description: 'Learn the final steps to complete your design',
		url: 'https://cdn.moonchild.ai/onboarding/preview-edit-export-1.mp4',
		thumbnail: 'https://cdn.moonchild.ai/onboarding/preview-edit-export-1.jpg',
		duration: '3:18',
	},
];

export type EntryId = LearningCenterVideo;

const LearningCenterModal = () => {
	const dispatch = useDispatch();
	const { state, user } = useSelector(
		(r: RootReducer) => ({
			state: r.mode.learningCenterState,
			user: selectUser(r.db),
		}),
		shallowEqual
	);

	// Determine which video to show based on the logic:
	// 1. If showOnboarding is true, show the 'intro' video
	// 2. If currentVideo is set, show that video
	// 3. Otherwise, show undefined (no video)
	let videoToShow: LearningCenterVideo | undefined;
	if (state.showOnboarding) {
		videoToShow = 'intro' as LearningCenterVideo;
	} else if (state.currentVideo) {
		videoToShow = state.currentVideo;
	} else {
		videoToShow = undefined;
	}

	const currentVideo = videoToShow ? ENTRIES.find((entry) => entry.id === videoToShow) : undefined;

	const videoRef = useRef<VideoPlayerRef>(null);
	const [hasInitialized, setHasInitialized] = useState(false);
	const [videoReady, setVideoReady] = useState(false);
	const [showEndVideoOverlay, setShowEndVideoOverlay] = useState(false);

	const isCompleted =
		videoToShow && user?.metadata.completedLearningCenterVideos?.includes(videoToShow);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	// Set video position and autoplay when modal loads
	useEffect(() => {
		if (videoRef.current && !hasInitialized && videoToShow && videoReady) {
			const savedPosition = state.videoPositions[videoToShow];

			const setupVideo = async () => {
				if (videoRef.current) {
					// Set position if we have one
					if (savedPosition && savedPosition > 0) {
						const closeToEnd = savedPosition && savedPosition > videoRef.current.duration - 5;
						if (state.autoplay && closeToEnd) {
							await videoRef.current.setCurrentTime(0);
						} else {
							await videoRef.current.setCurrentTime(savedPosition);
						}
					}
					if (state.autoplay) {
						try {
							await videoRef.current.play();
						} catch (error) {
							console.error('Autoplay failed (user interaction required):', error);
						}
						dispatch(setLearningCenterState({ autoplay: false }));
					}

					setHasInitialized(true);
				}
			};

			// Small delay to ensure video is ready
			setTimeout(setupVideo, 200);
		}
	}, [hasInitialized, videoReady]);

	// Reset initialization state when video changes
	useEffect(() => {
		setHasInitialized(false);
		setVideoReady(false);
		setShowEndVideoOverlay(false);
	}, [videoToShow]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onClose = useCallback(() => {
		trackLearningAction('video', 'close');
		// There are two reasons we could be showing the modal. We clear both conditions.
		dispatch(
			setLearningCenterState({
				showOnboarding: false,
				currentVideo: undefined,
				// when the panel is closed, that means our FTUE is responsible for showing the learning content
				// therefore we highlight the learning center button (to further educate the user)
				showMessage: !state.panelVisible ? true : false,
			})
		);
	}, [dispatch]);

	const markVideoAsCompleted = useCallback(
		async (video: LearningCenterVideo) => {
			trackLearningAction('video', 'complete', video);
			try {
				if (user && !isCompleted) {
					await forge.updateUser({
						update: {
							id: user.id,
							metadata: {
								completedLearningCenterVideos: [
									...(user?.metadata.completedLearningCenterVideos || []),
									video as LearningCenterVideo,
								],
							},
						},
					});
				}
			} catch (error) {
				console.error('Failed to mark video as completed:', error);
			}
		},
		[user, isCompleted]
	);

	const handleVideoEnded = () => {
		setShowEndVideoOverlay(true);
		videoToShow && markVideoAsCompleted(videoToShow as LearningCenterVideo);
	};

	const handleReplay = () => {
		if (videoRef.current) {
			videoRef.current.setCurrentTime(0);
			videoRef.current.play();
			trackLearningAction('video', 'replay', videoToShow);
			setShowEndVideoOverlay(false);
		}
	};

	const handleNextVideo = () => {
		const currentIndex = ENTRIES.findIndex((entry) => entry.id === videoToShow);
		if (currentIndex >= 0) {
			const nextVideo = ENTRIES[(currentIndex + 1) % ENTRIES.length];
			dispatch(setLearningCenterState({ currentVideo: nextVideo.id, autoplay: true }));
			trackLearningAction('video', 'next', videoToShow);
		}
		setShowEndVideoOverlay(false);
	};

	const handleVideoProgress = useCallback(() => {
		if (videoRef.current && videoToShow) {
			const currentTime = videoRef.current.currentTime;
			const savedPosition = state.videoPositions[videoToShow] || 0;
			// Only update if position has changed significantly (avoid unnecessary updates)
			if (Math.abs(currentTime - savedPosition) > 0.5) {
				dispatch(
					setLearningCenterState({
						videoPositions: {
							...state.videoPositions,
							[videoToShow]: currentTime,
						},
					})
				);
			}
		}
	}, [dispatch, state.videoPositions, videoToShow, hasInitialized]);

	const handleVideoCanPlay = useCallback(() => {
		setVideoReady(true);
	}, []);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<Modal title={''} maxWidth="1200px" onClose={onClose}>
			<S.Content>
				{state.showOnboarding && (
					<S.Onboarding>
						<S.OnboardingTitle>Get Started with Moonchild</S.OnboardingTitle>
						<S.OnboardingDescription>
							While your first designs generate, learn the essentials.
						</S.OnboardingDescription>
					</S.Onboarding>
				)}
				<S.Video>
					<S.VideoHeader>
						<S.VideoTitle>{currentVideo?.title || ''}</S.VideoTitle>
						{isCompleted && (
							<S.CompletedBadge>
								<Icon
									type={IconType.CHECKBOX_CIRCLE}
									color={unthemed.green[200]}
									width={20}
									height={20}
								/>
								Completed
							</S.CompletedBadge>
						)}
					</S.VideoHeader>
					<S.VideoContainer>
						<VideoPlayer
							ref={videoRef}
							src={currentVideo?.url || ''}
							onEnded={handleVideoEnded}
							onTimeUpdate={handleVideoProgress}
							onCanPlay={handleVideoCanPlay}
						/>
						{showEndVideoOverlay && (
							<S.EndOverlay onClick={() => setShowEndVideoOverlay(false)}>
								<S.EndOverlayContent>
									<S.EndOverlayTitle>Video Complete</S.EndOverlayTitle>
									<S.EndOverlayButtons>
										<Button
											onClick={handleReplay}
											state={ButtonState.REGULAR}
											type={ButtonType.SECONDARY}
										>
											Replay
										</Button>
										{state.showOnboarding ? (
											<Button
												onClick={onClose}
												state={ButtonState.REGULAR}
												type={ButtonType.PRIMARY}
											>
												Continue to Moonchild
											</Button>
										) : (
											<Button
												onClick={handleNextVideo}
												state={ButtonState.REGULAR}
												type={ButtonType.PRIMARY}
											>
												Next Video
											</Button>
										)}
									</S.EndOverlayButtons>
								</S.EndOverlayContent>
							</S.EndOverlay>
						)}
					</S.VideoContainer>
				</S.Video>
			</S.Content>
		</Modal>
	);
};

export default LearningCenterModal;
